import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Table,
  Typography,
  Upload,
} from "antd";
import {
  AFTER_DISBURSE_IOU_LIST,
  EXPORT_DOC_FEE_ADD_ENDPOINT,
  EXPORT_DOC_FEE_EDIT_ENDPOINT,
  IOU_COST_LIST_ENDPOINT,
  SALES_CONTRACT_COMMERCIAL_LIST,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import moment from "moment";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;

// Constants
const DEFAULT_HEADER_ID = 10;
const DEFAULT_CURRENCY = "BDT";

/**
 * Main Component
 */
const ExportDocumentationFeeForm = forwardRef((props, ref) => {
  const { form, isEdit, editInfo, viewMode } = props;
  const costWatcher = Form.useWatch("cost", form);
  const navigate = useNavigate();

  // Calculate total of all costs
  const total = costWatcher?.reduce((sum, cost) => {
    const amount = parseFloat(cost.exim_charge_amount) || 0;
    return sum + amount;
  }, 0);

  // State management
  const [options, setOptions] = useState({
    salesContracts: [],
    invoices: [], // Added for Invoice Number dropdown
    iou: [],
  });
  const [fileList, setFileList] = useState([]);

  // handle file upload change
  const handleFileChange = useCallback(({ fileList: newFileList }) => {
    setFileList(newFileList);
  }, []);

  const resetFiles = useCallback(() => {
    setFileList([]);
  }, []);

  const addFile = useCallback((file) => {
    setFileList((prev) => [...prev, file]);
  }, []);

  // Get Api service
  const getSalesContractNumber = useCallback(async (filter) => {
    const body = {
      page: 1,
      per_page: 10,
      ...(filter?.reference_no && { reference_no: filter.reference_no }),
    };
    const response = await getData(
      `${SALES_CONTRACT_COMMERCIAL_LIST}`,
      null,
      body,
    );
    if (response?.status === 200) {
      setOptions((prev) => ({
        ...prev,
        salesContracts: response?.data?.data?.data || [],
      }));
    }
  }, []);

  // Get Invoice Numbers
  const getInvoiceNumbers = useCallback(async () => {
    // Using the export invoice numbers API endpoint directly
    const response = await getData(`/api/accounting/export_invoice_numbers`);
    if (response?.status === 200) {
      const invoicesData = response?.data?.data || [];
      setOptions((prev) => ({
        ...prev,
        invoices: invoicesData,
      }));
    }
  }, []);

  // No longer needed since we removed account functionality

  const getCharges = useCallback(async () => {
    const response = await getData(
      `/api/exim-cost/getTypebyHead/?cost_head_id=${DEFAULT_HEADER_ID}`,
    );
    if (response?.status === 200) {
      const feeTypes = response?.data || [];

      const costFields = feeTypes.map((fee) => ({
        key: fee.id,
        type: fee.type.name,
        exim_charge_id: fee.id,
        exim_charge_amount: fee.amount || 0, // Default to 0 if amount is not provided
        isChecked: false,
      }));

      // Clear existing values first
      form.setFieldsValue({ cost: [] });
      form.setFieldsValue({ tabs: costFields });
    }
  }, [form]);

  const fetchInitialData = useCallback(async () => {
    await getIOU();
    if (!isEdit) {
      await getSalesContractNumber();
      await getInvoiceNumbers(); // Load invoice numbers directly
      await getCharges();
      resetFiles();
    }
  }, [
    isEdit,
    resetFiles,
    getCharges,
    getInvoiceNumbers,
    getSalesContractNumber,
  ]);

  // Initial get data on component mount
  useEffect(() => {
    fetchInitialData(isEdit);
  }, [isEdit, fetchInitialData]);

  // Separate effect to handle editInfo changes
  useEffect(() => {
    if (isEdit && editInfo) {
      // First, load all invoices to populate the dropdown
      getInvoiceNumbers();

      // Map existing costs to form fields based on the API structure
      const existingCosts = editInfo.lien_charges || [];
      const costFields = existingCosts.map((charge) => ({
        key: charge.exim_charge_id,
        type: charge.charges.type.name,
        exim_charge_id: charge.exim_charge_id,
        exim_charge_amount: charge.exim_charge_amount,
      }));

      // Get fee types and then manually set the selected ones
      getCharges().then(() => {
        // Form values based on the API response structure
        form.setFieldsValue({
          invoice_id: editInfo.invoice_id,
          sales_contract_number:
            editInfo.export_details?.sales_contract_info?.reference_no || "",
          sales_contract_id: editInfo.export_details?.sales_contract_id,
          quantity: editInfo.export_details?.total_quantity,
          value: editInfo.export_details?.invoice_value,
          trn_date: editInfo.trn_date ? moment(editInfo.trn_date) : null,
          cost: costFields,
          iou_id: editInfo?.iou_info?.id,
        });
      });

      if (editInfo?.iou_info) {
        const iouExists =
          options.iou.filter((iou) => iou.id === editInfo.iou_info.id).length >
          0;

        if (!iouExists) {
          setOptions((prev) => ({
            ...prev,
            iou: [
              {
                id: editInfo.iou_info.id,
                iou_number: editInfo.iou_info.iou_number,
              },
              ...prev.iou,
            ],
          }));
        }
      }

      // Get sales contract details if available
      if (editInfo.export_details?.sales_contract_info?.reference_no) {
        getSalesContractNumber({
          reference_no:
            editInfo.export_details?.sales_contract_info?.reference_no,
        });
      }

      // Handle voucher file
      if (editInfo?.voucher) {
        // Clear existing files first to avoid duplication
        setFileList([]);
        addFile({
          uid: "-1",
          name: editInfo.voucher.split("/").pop(),
          status: "done",
          url: process.env.REACT_APP_BASE_URL + "/" + editInfo.voucher,
        });
      }
    }
  }, [
    editInfo,
    isEdit,
    form,
    addFile,
    getInvoiceNumbers,
    getSalesContractNumber,
    getCharges,
    options.iou,
  ]);

  // Handle invoice selection directly without needing to search or select sales contracts

  // Handle invoice selection
  const onSelectInvoice = useCallback(
    async (value) => {
      try {
        const selectedInvoice = options.invoices.find(
          (invoice) => invoice.id === value,
        );

        if (selectedInvoice) {
          // Set the form values based on the JSON structure
          const formValues = {
            sales_contract_id: selectedInvoice.sales_contract_id,
            sales_contract_number:
              selectedInvoice.sales_contract_info?.reference_no || "",
            quantity: selectedInvoice.total_quantity || "",
            value: selectedInvoice.invoice_value || "",
          };

          // Set the form values
          form.setFieldsValue(formValues);

          // Load fee types when an invoice is selected
          await getCharges();
        }
      } catch (error) {}
    },
    [options.invoices, form, getCharges],
  );

  // Form submission
  const handleSubmit = useCallback(
    async (values) => {
      try {
        // Format cost items for submission based on the API structure
        const costs =
          values.cost?.map((cost) => ({
            exim_charge_id: cost.exim_charge_id,
            exim_charge_amount: cost.exim_charge_amount,
          })) || [];

        // Create FormData for submission
        const formData = new FormData();

        // Add form fields to FormData
        formData.append("invoice_id", values.invoice_id);

        // formData.append("iou_id", values.iou_id);
        if(values?.iou_id) {
          formData.append("iou_id", values.iou_id);
        }

        // Only include sales_contract_id if it exists
        if (values.sales_contract_id) {
          formData.append("sales_contract_id", values.sales_contract_id);
        }

        // Add TRN date
        if (values.trn_date) {
          formData.append("trn_date", values.trn_date.format("YYYY-MM-DD"));
        }

        // Add cost array as JSON
        formData.append("cost", JSON.stringify(costs));

        // Add total amount
        if (total) {
          formData.append("total", total);
        }

        // Add voucher file if present
        if (fileList.length > 0) {
          // Check if it's a new file or an existing one
          if (fileList[0].originFileObj) {
            formData.append("voucher", fileList[0].originFileObj);
          } else if (isEdit && !fileList[0].originFileObj) {
            // For edit mode, if no new file is uploaded, we keep the existing one
            // No need to append anything as backend will keep the existing file
          }
        }

        // Determine endpoint based on edit mode
        const query = isEdit
          ? `${EXPORT_DOC_FEE_EDIT_ENDPOINT}/${editInfo?.id}`
          : EXPORT_DOC_FEE_ADD_ENDPOINT;

        // Submit form data
        const response = await postData(query, formData);

        if (response && (response?.code === 200 || response?.status === 200)) {
          notification.success({
            message: "Success",
            description:
              response?.message ||
              `Export Documentation Fee ${
                isEdit ? "updated" : "created"
              } successfully`,
            placement: "topRight",
          });
          navigate("/import-export-expense-tracking/export-doc-fee");
        }
      } catch (error) {}
    },
    [fileList, isEdit, editInfo, navigate, total],
  );

  // Expose form instance
  useImperativeHandle(ref, () => ({ form }), [form]);

  // Table columns configuration
  const tableColumns = useMemo(
    () => [
      {
        title: "Title",
        dataIndex: "fieldName",
        key: "id",
        width: "40%",
        render: (fieldName) => (
          <>
            <Form.Item
              name={[fieldName, "type"]}
              style={{ width: "100%", marginBottom: "0" }}
              rules={[
                { required: true, message: "Please select a Description" },
              ]}
            >
              <Input disabled={true} />
            </Form.Item>
          </>
        ),
      },
      {
        title: "Total",
        dataIndex: "fieldName",
        key: "exim_charge_amount",
        width: "60%",
        render: (fieldName) => (
          <Form.Item
            name={[fieldName, "exim_charge_amount"]}
            noStyle
            rules={[
              {
                required: true,
                message: "Please enter amount",
              },
            ]}
          >
            <InputNumber
              placeholder="Enter amount"
              disabled={viewMode}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              min={0}
              precision={2}
              style={{ width: "100%" }}
            />
          </Form.Item>
        ),
      },
    ],
    [viewMode],
  );

  const getIOU = async (filter) => {
    const body = {
      page: 1,
      per_page: 100,
      ...(filter?.iou_number && { iou_number: filter.iou_number }),
    };
    const response = await getData(`${AFTER_DISBURSE_IOU_LIST}`, null, body);

    if (response?.status === 200) {
      setOptions((prev) => ({
        ...prev,
        iou: response?.data?.data || [],
      }));
    }
  };

  return (
    <div className="containt-body">
      <Card
        title={""}
        bordered={false}
        style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={true}
        >
          <Card
            title={<Text strong>Invoice Information</Text>}
            style={{ marginBottom: "24px", borderRadius: "8px" }}
            size="small"
          >
            <Row gutter={24}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item
                  label="Invoice Number"
                  name="invoice_id"
                  rules={[
                    {
                      required: true,
                      message: "Please select an invoice number",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select invoice"
                    onSelect={onSelectInvoice}
                    disabled={viewMode}
                    showSearch
                    filterOption={(input, option) =>
                      option.label.toLowerCase().indexOf(input.toLowerCase()) >=
                      0
                    }
                    options={options.invoices.map((invoice) => ({
                      label: invoice.invoice_number,
                      value: invoice.id,
                    }))}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item
                  label="Sales Contract Number"
                  name="sales_contract_number"
                >
                  <Input
                    disabled={true}
                    placeholder="Auto-populated from Invoice"
                    style={{ background: "#f5f5f5", color: "#333" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Quantity" name="quantity">
                  <Input
                    disabled={true}
                    placeholder="Auto-filled from Invoice"
                    style={{ background: "#f5f5f5", color: "#333" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Value" name="value">
                  <Input
                    disabled={true}
                    placeholder="Auto-filled from Invoice"
                    style={{ background: "#f5f5f5", color: "#333" }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item label="TRN Date" name="trn_date">
                  <DatePicker
                    style={{ width: "100%" }}
                    disabled={viewMode}
                    placeholder="Select TRN Date"
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="IOU"
                  name="iou_id"
                  rules={[{ required: false, message: "Please select an IOU" }]}
                >
                  <Select
                    placeholder="Select IOU"
                    disabled={viewMode}
                    showSearch
                    filterOption={false} // Disable default filtering
                    onSearch={async (value) => {
                      if (value && value.length >= 2) {
                        await getIOU({ iou_number: value }); // Fetch IOU data based on search input
                      } else if (!value) {
                        await getIOU(); // Fetch default IOU data
                      }
                    }}
                    onSelect={(value) => {
                      const selectedIOU = options.iou.find(
                        (iou) => iou.id === value,
                      );
                      if (selectedIOU) {
                        form.setFieldsValue({
                          iou_id: selectedIOU.id,
                        });
                      }
                    }}
                    options={options.iou.map((iou) => ({
                      label: iou.iou_number,
                      value: iou.id,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label="Voucher"
                  name="voucher"
                  rules={[
                    {
                      // required: !isEdit,
                      required: false,
                      message: "Please upload a voucher file",
                    },
                  ]}
                >
                  <Upload
                    fileList={fileList}
                    onChange={handleFileChange}
                    beforeUpload={() => false}
                    maxCount={1}
                    disabled={viewMode}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  >
                    <Button
                      icon={<UploadOutlined />}
                      disabled={viewMode}
                      type="default"
                      style={{
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isEdit && fileList.length > 0
                        ? "Change File"
                        : "Upload Voucher"}
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Row>
            <Col span={24}>
              {form.getFieldValue("tabs")?.length === 0 && (
                <Text type="warning">
                  No fee types available. Please select an invoice first or
                  contact admin.
                </Text>
              )}
              {form.getFieldValue("tabs")?.length > 0 && (
                <div
                  className="fee-tabs-container"
                  style={{
                    padding: "12px",
                    background: "#f9f9f9",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      marginBottom: "12px",
                      paddingBottom: "8px",
                      borderBottom: "1px solid #e8e8e8",
                      display: "flex",
                      gap: "4px",
                    }}
                  >
                    <Text type="Warning">Select Fee Types:</Text>
                    <Text type="danger">
                      Check the boxes below to select fee types. You can select
                      multiple fees.
                    </Text>
                  </div>
                  <div
                    className="fee-tabs-header"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "12px",
                      marginBottom: "12px",
                      paddingBottom: "4px",
                    }}
                  >
                    <Checkbox.Group
                      onChange={(checkedValues) => {
                        const allTabs = form.getFieldValue("tabs") || [];
                        // Get current selected fees to preserve their values
                        const currentCost = form.getFieldValue("cost") || [];

                        // Create a map of current amounts by exim_charge_id for easy lookup
                        const amountMap = {};
                        currentCost.forEach((item) => {
                          amountMap[item.exim_charge_id] =
                            item.exim_charge_amount;
                        });

                        // For each selected fee, either keep its current value or use default
                        const selectedFees = allTabs
                          .filter((fee) => checkedValues.includes(fee.key))
                          .map((fee) => ({
                            key: fee.key,
                            type: fee.type,
                            exim_charge_id: fee.id || fee.key,
                            // If this fee was previously selected, keep its amount; otherwise use default
                            exim_charge_amount:
                              amountMap[fee.key] !== undefined
                                ? amountMap[fee.key]
                                : fee.exim_charge_amount || 0,
                          }));

                        // Update the form
                        form.setFieldsValue({ cost: selectedFees });
                      }}
                      value={
                        form
                          .getFieldValue("cost")
                          ?.map((fee) => fee.exim_charge_id) || []
                      }
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        width: "100%",
                        gap: "12px",
                      }}
                    >
                      {form.getFieldValue("tabs").map((fee) => {
                        const isSelected = form
                          .getFieldValue("cost")
                          ?.some((item) => item.exim_charge_id === fee.key);

                        return (
                          <Checkbox
                            key={fee.key}
                            className={`fee-tab ${
                              isSelected ? "fee-tab-selected" : ""
                            }`}
                            style={{
                              padding: "8px 16px",
                              border: `1px solid ${
                                isSelected ? "#1890ff" : "#d9d9d9"
                              }`,
                              borderRadius: "6px",
                              cursor: "pointer",
                              backgroundColor: isSelected
                                ? "#e6f7ff"
                                : "#ffffff",
                              position: "relative",
                              marginRight: "8px",
                              display: "flex",
                              boxShadow: isSelected
                                ? "0 2px 6px rgba(24,144,255,0.2)"
                                : "none",
                              transition: "all 0.3s ease",
                              marginBottom: "10px",
                            }}
                            value={fee.key}
                            disabled={viewMode}
                          >
                            <span
                              style={{
                                fontWeight: isSelected ? "500" : "normal",
                                color: isSelected ? "#1890ff" : "#333",
                              }}
                            >
                              {fee.type}
                            </span>
                          </Checkbox>
                        );
                      })}
                    </Checkbox.Group>
                  </div>
                </div>
              )}
            </Col>
          </Row>
          <Divider orientation="left">Fee Details</Divider>
          <Form.List name="cost">
            {(fields) => (
              <>
                {fields.length > 0 ? (
                  <Table
                    bordered
                    dataSource={fields.map((field) => ({
                      ...field,
                      key: field.key,
                      fieldName: field.name,
                    }))}
                    pagination={false}
                    columns={tableColumns}
                    summary={() => (
                      <Table.Summary fixed>
                        <Table.Summary.Row style={{ background: "#f0f7ff" }}>
                          <Table.Summary.Cell index={0} colSpan={1}>
                            <Text strong style={{ fontSize: "16px" }}>
                              Grand Total
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} colSpan={4}>
                            <Text strong>
                              {parseFloat(total || 0).toFixed(2)}{" "}
                              {DEFAULT_CURRENCY}
                            </Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                    style={{ marginBottom: "24px" }}
                  />
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "24px 0",
                      background: "#fafafa",
                      borderRadius: "8px",
                      border: "1px dashed #d9d9d9",
                      marginBottom: "24px",
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: "14px" }}>
                      Please select at least one fee type from above to add
                      details
                    </Text>
                  </div>
                )}
              </>
            )}
          </Form.List>
        </Form>
      </Card>
    </div>
  );
});

ExportDocumentationFeeForm.displayName = "ExportDocumentationFeeForm";

export default ExportDocumentationFeeForm;
